import { inject, Injectable, Injector, runInInjectionContext, signal } from '@angular/core';
import { KEY_WORD_AUTHENTICATED_USER } from '@core/constants';
import {
  AuthenticatedUser,
  MeDocument,
  MeQuery,
  Permissions,
  RefreshAuthRoleGQL,
  RefreshAuthRoleQuery,
  RoleEntityContextInput,
  RoleTypeEnum,
} from '@generated/graphql';
import { PermissionsService } from '../permissions.service';
import { DebugableService } from '../debug/debug.type';
import { Apollo } from 'apollo-angular';
import { ActivationEnd, Router } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { jwtDecode } from 'jwt-decode';
import { SocialAuthService } from '@abacritt/angularx-social-login';
import { safeJsonParse } from '@core/utils/safe-json-parse';
import { urlSnapshotToRedirectPath } from '@core/utils/url-snapshot-to-redirect-path';
import { GleapService } from '../gleap.service';

@Injectable({ providedIn: 'root' })
export class AuthService implements DebugableService {
  public readonly ROLE_HIERARCHY: Record<RoleTypeEnum, RoleTypeEnum[]> = {
    // Global
    [RoleTypeEnum.GlobalOwner]: [
      RoleTypeEnum.GlobalAdmin,
      RoleTypeEnum.GlobalMember,
      RoleTypeEnum.GlobalGuest,
    ],
    [RoleTypeEnum.GlobalAdmin]: [RoleTypeEnum.GlobalMember, RoleTypeEnum.GlobalGuest],
    [RoleTypeEnum.GlobalMember]: [RoleTypeEnum.GlobalGuest],
    [RoleTypeEnum.GlobalGuest]: [],

    // Pipe
    [RoleTypeEnum.PipeOwner]: [
      RoleTypeEnum.PipeSupervisor,
      RoleTypeEnum.PipeEditor,
      RoleTypeEnum.PipeEditorLimited,
      RoleTypeEnum.PipeReader,
    ],
    [RoleTypeEnum.PipeSupervisor]: [
      RoleTypeEnum.PipeEditor,
      RoleTypeEnum.PipeEditorLimited,
      RoleTypeEnum.PipeReader,
    ],
    [RoleTypeEnum.PipeEditor]: [RoleTypeEnum.PipeEditorLimited, RoleTypeEnum.PipeReader],
    [RoleTypeEnum.PipeEditorLimited]: [RoleTypeEnum.PipeReader],
    [RoleTypeEnum.PipeReader]: [],

    [RoleTypeEnum.DatabaseOwner]: [
      RoleTypeEnum.DatabaseAdmin,
      RoleTypeEnum.DatabaseEditor,
      RoleTypeEnum.DatabaseReader,
    ],
    [RoleTypeEnum.DatabaseAdmin]: [RoleTypeEnum.DatabaseEditor, RoleTypeEnum.DatabaseReader],
    [RoleTypeEnum.DatabaseEditor]: [RoleTypeEnum.DatabaseReader],
    [RoleTypeEnum.DatabaseReader]: [],
  };
  public gleapService = inject(GleapService);

  private readonly _authenticatedUser = signal<AuthenticatedUser | null>(null);
  public readonly authenticatedUser = this._authenticatedUser.asReadonly();

  private ignoreRedirects = false;

  public authenticatedUser$ = toObservable(this.authenticatedUser);

  private permissionMap: Map<
    string,
    {
      id: string;
      scope: string;
    }
  > = new Map();

  private pipeRole: Map<
    string,
    {
      type: RoleTypeEnum;
      permissions: Map<string, { id: string; scope: string }>;
    }
  > = new Map();

  public pipeRoleReactive = signal(0);

  private databaseRole: Map<
    string,
    {
      type: RoleTypeEnum;
      permissions: Map<string, { id: string; scope: string }>;
    }
  > = new Map();

  public databaseRoleReactive = signal(0);

  get isAuthenticated(): boolean {
    return Boolean(this.authenticatedUser());
  }

  get authenticatedUserPermissions(): Map<string, { id: string; scope: string }> {
    return this.permissionMap;
  }

  constructor(
    private readonly apollo: Apollo,
    private readonly refreshAuthRoleGQL: RefreshAuthRoleGQL,
    private readonly router: Router,
    private readonly injector: Injector,
  ) {
    this.setupRouterEvents();
    this.setup();
  }

  private setup() {
    const user = localStorage.getItem(KEY_WORD_AUTHENTICATED_USER);

    if (!user) {
      return;
    }

    try {
      const parsedUser = safeJsonParse<AuthenticatedUser>(user);
      if (!parsedUser) {
        this._authenticatedUser.set(null);
        this.logout();
        return;
      }

      this.gleapService.setIdentify(parsedUser);
      this._authenticatedUser.set(parsedUser);
      const areTokensValid = this.areTokensValid();
      if (!areTokensValid) {
        throw new Error('Tokens are invalid');
      }
    } catch {
      this._authenticatedUser.set(null);
      this.logout();
    }
  }

  public isCurrentUser(id: string | null): boolean {
    return this.authenticatedUser()?.user?.id === id;
  }

  public setAuthenticatedUser(user: AuthenticatedUser): void {
    this._authenticatedUser.set(user);
    localStorage.setItem(KEY_WORD_AUTHENTICATED_USER, JSON.stringify(user));

    if (Array.isArray(user?.role?.permissions?.nodes)) {
      for (const permission of user.role.permissions.nodes) {
        this.permissionMap.set(permission!.id, {
          id: permission!.id,
          scope: permission!.scope,
        });
      }
    }
  }

  public pipeRolePermissions(pipeId: string): Map<string, { id: string; scope: string }> {
    return this.pipeRole.get(pipeId)?.permissions ?? new Map();
  }

  public databaseRolePermissions(databaseId: string): Map<string, { id: string; scope: string }> {
    return this.databaseRole.get(databaseId)?.permissions ?? new Map();
  }

  public setPipeRole(
    pipeId: string,
    role: NonNullable<NonNullable<RefreshAuthRoleQuery['refreshAuthRole']>['pipeRole']>,
  ): void {
    this.pipeRole.set(pipeId, {
      type: role.type,
      permissions: PermissionsService.overrideGlobalPermissionsWithScopedPermissions(
        Array.from(this.permissionMap.values()) as Permissions[],
        role.permissions.nodes as Permissions[],
      ),
    });
    this.pipeRoleReactive.set(this.pipeRole.size);
  }

  public setDatabaseRole(
    databaseId: string,
    role: NonNullable<NonNullable<RefreshAuthRoleQuery['refreshAuthRole']>['databaseRole']>,
  ): void {
    this.databaseRole.set(databaseId, {
      type: role.type,
      permissions: PermissionsService.overrideGlobalPermissionsWithScopedPermissions(
        Array.from(this.permissionMap.values()) as Permissions[],
        role.permissions.nodes as Permissions[],
      ),
    });
    this.databaseRoleReactive.set(this.databaseRole.size);
  }

  public isAuthenticatedUserAboveRole(roleType: string): boolean {
    if (!this.authenticatedUser()?.role?.type) {
      return false;
    }

    if ([RoleTypeEnum.GlobalOwner, RoleTypeEnum.PipeOwner].includes(roleType as RoleTypeEnum)) {
      return false;
    }

    const userRole = this.authenticatedUser()!.role!.type;
    const roleHierarchy = this.ROLE_HIERARCHY[userRole as RoleTypeEnum];

    return roleHierarchy.includes(roleType as RoleTypeEnum);
  }

  public rolesBelowAuthenticatedUser(): RoleTypeEnum[] {
    if (!this.authenticatedUser()?.role?.type) {
      return [];
    }

    return this.ROLE_HIERARCHY[this.authenticatedUser()!.role!.type];
  }

  public rolesBelowAuthenticatedUserForPipe(pipeId: string): RoleTypeEnum[] {
    if (!this.authenticatedUser()?.role) {
      return [];
    }

    const pipeRole = this.pipeRole.get(pipeId);

    if (!pipeRole) {
      return [];
    }

    return this.ROLE_HIERARCHY[pipeRole.type];
  }

  public rolesBelowAuthenticatedUserForDatabase(databaseId: string): RoleTypeEnum[] {
    if (!this.authenticatedUser()?.role) {
      return [];
    }

    const databaseRole = this.databaseRole.get(databaseId);

    if (!databaseRole) {
      return [];
    }

    return this.ROLE_HIERARCHY[databaseRole.type];
  }

  public async assertRoleIsCached(roleEntityContext?: RoleEntityContextInput): Promise<boolean> {
    const isCached = this.getCacheBasedOnRoleEntityContext(roleEntityContext);

    if (!isCached) {
      const response = await firstValueFrom(
        this.refreshAuthRoleGQL.fetch({
          roleEntityContext: roleEntityContext,
        }),
      ).catch(() => null);

      if (response?.data.refreshAuthRole?.pipeRole) {
        this.setPipeRole(roleEntityContext?.pipeId ?? '', response.data.refreshAuthRole.pipeRole!);
        return true;
      }

      if (response?.data.refreshAuthRole?.databaseRole) {
        this.setDatabaseRole(
          roleEntityContext?.databaseId ?? '',
          response.data.refreshAuthRole.databaseRole!,
        );
        return true;
      }
    }

    return Boolean(isCached);
  }

  public async roleInPipe(pipeId: string, roles: `${RoleTypeEnum}`[]): Promise<boolean> {
    const meCached = this.apollo.client.cache.readQuery<MeQuery>({
      query: MeDocument,
      variables: { params: { pipeId } },
    });

    if (meCached?.me?.roleInPipe && roles.includes(meCached.me.roleInPipe)) {
      return true;
    }

    if (!meCached) {
      const response = await this.apollo.client.query<MeQuery>({
        query: MeDocument,
        variables: {
          params: {
            pipeId: pipeId,
          },
        },
      });

      if (response.data.me?.roleInPipe && roles.includes(response.data.me.roleInPipe)) {
        return true;
      }
    }

    return false;
  }

  public getRealtimeToken(): string | null {
    return this.authenticatedUser()?.realtimeToken ?? null;
  }

  async logout(): Promise<void> {
    await runInInjectionContext(this.injector, async () => {
      console.trace('Logging out');

      const socialAuthService = inject(SocialAuthService);
      this._authenticatedUser.set(null);
      this.permissionMap.clear();
      this.pipeRole.clear();
      this.gleapService.clearIdentify();

      localStorage.removeItem(KEY_WORD_AUTHENTICATED_USER);

      socialAuthService.signOut(true).catch(() => null);
      this.apollo.client.stop();

      setTimeout(async () => {
        try {
          if (this.router.url !== '/entrar' && !this.ignoreRedirects) {
            await this.router.navigate(['/entrar'], {
              queryParams: urlSnapshotToRedirectPath(),
            });
          }

          await this.apollo.client.clearStore().catch(() => null);
        } catch {
          void 0;
        }
      }, 750);
    });
  }

  public generateDebugReport() {
    return {
      authenticatedUser: this.authenticatedUser(),
      permissionMap: Array.from(this.permissionMap.entries()).map(([key, value]) => ({
        ...value,
        __map_key: key,
      })),
      pipeRole: Array.from(this.pipeRole.entries()).map(([key, value]) => ({
        pipeId: key,
        role: value.type,
        permissions: Array.from(value.permissions.entries()).map(([key, value]) => ({
          ...value,
          __map_key: key,
        })),
      })),
      rolesBelowAuthenticatedUser: this.rolesBelowAuthenticatedUser(),
      rolesBelowAuthenticatedUserForPipe: Array.from(this.pipeRole.keys()).reduce(
        (acc, pipeId) => ({
          ...acc,
          [pipeId]: this.rolesBelowAuthenticatedUserForPipe(pipeId),
        }),
        {},
      ),
    };
  }

  public getAccountId(): string | null {
    return this.authenticatedUser()?.accountId ?? null;
  }

  private areTokensValid(): boolean {
    const user = this.authenticatedUser();
    if (!user?.token || !user?.realtimeToken) {
      return false;
    }

    try {
      const decodedRealtimeToken = jwtDecode<{ exp: number }>(user.realtimeToken);
      const decodedToken = jwtDecode<{ exp: number }>(user.token);
      const now = Date.now() / 1000;

      const tokenValid = decodedToken.exp > now;
      const realtimeTokenValid = decodedRealtimeToken.exp > now;

      return tokenValid && realtimeTokenValid;
    } catch {
      return false;
    }
  }

  private setupRouterEvents(): void {
    this.router.events
      .pipe(
        filter((event): event is ActivationEnd => event instanceof ActivationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((activationEnd: ActivationEnd) => {
        this.ignoreRedirects = activationEnd.snapshot.data['ignoreAuthErrors'] ?? false;
      });
  }

  private getCacheBasedOnRoleEntityContext(
    roleEntityContext?: RoleEntityContextInput,
  ): Map<string, { id: string; scope: string }> | null {
    if (roleEntityContext?.pipeId) {
      return this.pipeRole.get(roleEntityContext.pipeId)?.permissions ?? null;
    }

    if (roleEntityContext?.databaseId) {
      return this.databaseRole.get(roleEntityContext.databaseId)?.permissions ?? null;
    }

    return null;
  }
}
